import type {
  ForecastData,
  ForecastPoint,
  GroupKpis,
  ServiceTimelineOps,
  ServiceTimelineReservation,
  ServiceTimelineSlot,
} from '../types';

const CANCELLED = new Set([3, 6, 7, 14, 16]);
const TWO_HOURS_H = 2;

export type TimelineOutletOption = {
  id: string;
  name: string;
  seated: number;
  closed?: boolean;
  city?: string;
  region?: string;
  /** Present when the live outlet snapshot is available — used for KPI columns. */
  seats?: number;
  tables?: number;
  occTables?: number;
  coversToday?: number;
  upcoming?: number;
  bookedAhead?: number;
  avgSeatMin?: number;
  noShowCovers?: number;
  cancelCovers?: number;
  /** Recovery ledger: cancels/no-shows with a table; lost = unrebooked after slot passed. */
  gapCovers?: number;
  lostGapCovers?: number;
  spendCover?: number;
  noShow?: number;
  cancelRate?: number;
  sellRevenue?: number;
  sellCount?: number;
  sellAttach?: number;
};

export type TimelineDayTotals = {
  /** Guests whose booking start is at/before now. */
  started: number;
  /** Guests still due after now. */
  ahead: number;
  /** Guests due in the next 2 hours. */
  next2h: number;
  /** started + ahead — booked by close. */
  total: number;
};

/** Stub from timelineOutletOptions when reservation.outletId misses live outlets. */
export function isStubOutlet(
  outlet: TimelineOutletOption | null | undefined,
): boolean {
  if (!outlet) return false;
  return (outlet.seats ?? 0) <= 0 && (outlet.tables ?? 0) <= 0;
}

/** Full fleet list for Live restaurant pick — open first, then name. */
export function sortLiveOutletOptions<
  T extends { name: string; closed?: boolean },
>(outlets: T[]): T[] {
  return [...outlets].sort((a, b) => {
    const ac = a.closed ? 1 : 0;
    const bc = b.closed ? 1 : 0;
    if (ac !== bc) return ac - bc;
    return a.name.localeCompare(b.name);
  });
}

/** Map ms → forecast service-hour axis (late-night 0–5h → +24). */
export function msToServiceHour(
  ms: number,
  openH: number,
  closeH: number,
): number {
  const d = new Date(ms);
  let h = d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
  if (h < 6 && (closeH > 24 || openH >= 12)) h += 24;
  return h;
}

/**
 * Active reservations for an outlet. When the selected live outlet id does not
 * appear on any bar but the board is a single foreign outletId (ID skew between
 * snapshot and timeline), attribute the whole board to the selection.
 */
export function reservationsForOutlet(
  timeline: ServiceTimelineOps | null | undefined,
  outletId: string | null,
): ServiceTimelineReservation[] {
  const all = (timeline?.reservations ?? []).filter(
    r => r.peopleCount > 0 && !CANCELLED.has(r.state),
  );
  if (!outletId) return all;
  const matched = all.filter(r => r.outletId === outletId);
  if (matched.length > 0) return matched;
  const foreignIds = new Set(all.map(r => r.outletId));
  if (foreignIds.size === 1) return all;
  return matched;
}

export function dayTotalsFromReservations(
  reservations: ServiceTimelineReservation[],
  openH: number,
  closeH: number,
  nowH: number,
): TimelineDayTotals {
  let started = 0;
  let ahead = 0;
  let next2h = 0;
  for (const r of reservations) {
    const startH = msToServiceHour(r.startMs, openH, closeH);
    if (startH <= nowH) {
      started += r.peopleCount;
    } else {
      ahead += r.peopleCount;
      if (startH <= nowH + TWO_HOURS_H) next2h += r.peopleCount;
    }
  }
  return { started, ahead, next2h, total: started + ahead };
}

/** Concurrent guests + live tables overlapping "now" on the timeline. */
export function liveFloorFromReservations(
  reservations: ServiceTimelineReservation[],
  openH: number,
  closeH: number,
  nowH: number,
): { guests: number; tables: number } {
  let guests = 0;
  const tableKeys = new Set<string>();
  let unassigned = 0;
  for (const r of reservations) {
    const startH = msToServiceHour(r.startMs, openH, closeH);
    const endH = Math.max(
      startH + 0.25,
      msToServiceHour(r.endMs, openH, closeH),
    );
    if (startH > nowH || endH <= nowH) continue;
    guests += r.peopleCount;
    const names = (r.tableNames ?? []).filter(Boolean);
    if (names.length > 0) {
      for (const n of names) tableKeys.add(String(n));
    } else {
      unassigned += 1;
    }
  }
  return { guests, tables: tableKeys.size + unassigned };
}

/**
 * Outlets for the Live picker. Prefer snapshot outlets (they carry capacity).
 * Never invent zero-capacity stubs when a real outlet exists — timeline ID skew
 * is handled by reservationsForOutlet instead.
 */
export function timelineOutletOptions(
  outlets: TimelineOutletOption[],
  timeline: ServiceTimelineOps | null | undefined,
): TimelineOutletOption[] {
  const ids = new Set(
    (timeline?.reservations ?? []).map(r => r.outletId).filter(Boolean),
  );
  const matched = outlets.filter(o => ids.size === 0 || ids.has(o.id));
  const fromOutlets = matched.length > 0 ? matched : outlets.slice();

  const options: TimelineOutletOption[] = [...fromOutlets];

  // Only when the live payload has no outlets at all (denied / empty scope).
  if (options.length === 0) {
    const seen = new Set<string>();
    for (const r of timeline?.reservations ?? []) {
      if (!r.outletId || seen.has(r.outletId)) continue;
      options.push({
        id: r.outletId,
        name: r.outletName || r.outletId,
        seated: 0,
        seats: 0,
        tables: 0,
        occTables: 0,
        coversToday: 0,
        upcoming: 0,
      });
      seen.add(r.outletId);
    }
  }

  return options.sort((a, b) => {
    const ac = a.closed ? 1 : 0;
    const bc = b.closed ? 1 : 0;
    if (ac !== bc) return ac - bc;
    return a.name.localeCompare(b.name);
  });
}

/** Snapshot outlet with real seat/table capacity, if any. */
export function capacityOutlet(
  outlets: TimelineOutletOption[],
  preferredId: string | null = null,
): TimelineOutletOption | null {
  const withCapacity = (o: TimelineOutletOption) =>
    !isStubOutlet(o) && ((o.seats ?? 0) > 1 || (o.tables ?? 0) > 0);
  if (preferredId) {
    const preferred = outlets.find(o => o.id === preferredId && withCapacity(o));
    if (preferred) return preferred;
  }
  return outlets.find(withCapacity) ?? outlets.find(o => !isStubOutlet(o)) ?? null;
}

export function filterTimelineForOutlet(
  timeline: ServiceTimelineOps | null | undefined,
  outletId: string | null,
): ServiceTimelineOps | null {
  if (!timeline) return null;
  if (!outletId) return timeline;
  const reservations = reservationsForOutlet(timeline, outletId);
  const shiftIds = new Set(reservations.map(r => r.outletId));
  shiftIds.add(outletId);
  const shifts = (timeline.shifts ?? []).filter(s => shiftIds.has(s.outletId));
  const slots = slotsFromReservations(reservations, timeline.slots);
  return { slots, reservations, shifts };
}

function slotsFromReservations(
  reservations: ServiceTimelineReservation[],
  fallbackSlots: ServiceTimelineSlot[],
): ServiceTimelineSlot[] {
  const active = reservations.filter(
    r => r.peopleCount > 0 && !CANCELLED.has(r.state),
  );
  if (active.length === 0) {
    return fallbackSlots.map(s => ({ ...s, guests: 0, capacity: null }));
  }
  const step = 15 * 60_000;
  const minMs = Math.min(...active.map(r => r.startMs));
  const maxMs = Math.max(...active.map(r => r.endMs));
  const origin = Math.floor(minMs / step) * step;
  const slots: ServiceTimelineSlot[] = [];
  for (let t = origin; t < maxMs; t += step) {
    let guests = 0;
    for (const r of active) {
      if (r.startMs < t + step && r.endMs > t) guests += r.peopleCount;
    }
    if (guests > 0) {
      slots.push({ timeMs: t, guests, capacity: null, locked: false });
    }
  }
  return slots.length ? slots : fallbackSlots.map(s => ({ ...s, guests: 0 }));
}

/** Per-outlet guest curve sharing the scope forecast's service window. */
export function outletGuestForecast(
  base: ForecastData,
  timeline: ServiceTimelineOps | null,
  outletId: string | null,
  outlet: TimelineOutletOption | null = null,
): ForecastData {
  if (!outletId || !timeline) return base;
  const res = reservationsForOutlet(timeline, outletId);

  const pts: ForecastPoint[] = base.pts.map(p => {
    const h0 = p.h;
    const h1 = p.h + 1;
    let guests = 0;
    let arrivals = 0;
    for (const r of res) {
      const startH = msToServiceHour(r.startMs, base.openH, base.closeH);
      const endH = Math.max(
        startH + 0.25,
        msToServiceHour(r.endMs, base.openH, base.closeH),
      );
      if (startH < h1 && endH > h0) guests += r.peopleCount;
      if (startH >= h0 && startH < h1) arrivals += r.peopleCount;
    }
    return { ...p, guests, covers: arrivals, occ: 0 };
  });

  let peak = pts[0] ?? { ...base.peak, guests: 0, covers: 0, occ: 0 };
  for (const p of pts) {
    if ((p.guests ?? 0) > (peak.guests ?? 0)) peak = p;
  }

  const fromTimeline = dayTotalsFromReservations(
    res,
    base.openH,
    base.closeH,
    base.nowH,
  );
  // Outlet snapshot can be a zeroed stub or closed (bookedAhead wiped). Never
  // let an explicit 0 beat a timeline that clearly has the day book.
  const coversToday = Math.max(outlet?.coversToday ?? 0, fromTimeline.started);
  const stillToCome = Math.max(
    outlet?.bookedAhead ?? outlet?.upcoming ?? 0,
    fromTimeline.ahead,
  );
  const projectedCovers = coversToday + stillToCome;
  const spend = outlet?.spendCover ?? 0;

  return {
    ...base,
    pts,
    peak,
    peakMin: Math.floor(peak.h) * 60 + 30,
    currentCovers: coversToday,
    projectedCovers,
    projRevenue: Math.round(projectedCovers * spend),
    noShowCovers: outlet?.noShowCovers ?? 0,
    cancelCovers: outlet?.cancelCovers ?? 0,
    atRisk: 0,
  };
}

/**
 * Scope ribbon KPIs to one outlet.
 * Right now = taken (live floor) vs available (outlet capacity).
 * Day book = max(outlet snapshot, timeline totals).
 */
export function ribbonKpisForOutlet(
  base: GroupKpis,
  outlet: TimelineOutletOption | null,
  dayTotals?: Pick<TimelineDayTotals, 'started' | 'next2h'> | null,
  liveFloor?: { guests: number; tables: number } | null,
): GroupKpis {
  const takenSeats = liveFloor?.guests ?? 0;
  const takenTables = liveFloor?.tables ?? 0;

  if (!outlet || outlet.coversToday == null || outlet.seats == null) {
    if (!dayTotals && !liveFloor) return base;
    const seats = Math.max(base.seats, 1);
    const tables = Math.max(base.tables, 0);
    const seated = takenSeats > 0 ? takenSeats : base.seated;
    const occTables = takenTables > 0 ? takenTables : base.occTables;
    return {
      ...base,
      covers: Math.max(base.covers, dayTotals?.started ?? 0),
      incoming: Math.max(base.incoming, dayTotals?.next2h ?? 0),
      seated,
      seats,
      occTables,
      tables,
      occupancy: Math.min(100, Math.round((seated / seats) * 100)),
      efficiency: +(seated / (occTables || 1)).toFixed(2),
    };
  }

  // Available = restaurant capacity. Taken = live floor (or snapshot seated).
  const seats = Math.max(outlet.seats ?? 0, 1);
  const tables = outlet.tables ?? 0;
  const seated = takenSeats > 0 ? takenSeats : outlet.seated;
  const occTables = takenTables > 0 ? takenTables : (outlet.occTables ?? 0);
  const covers = Math.max(outlet.coversToday, dayTotals?.started ?? 0);
  const incoming = Math.max(outlet.upcoming ?? 0, dayTotals?.next2h ?? 0);
  const seatTime = outlet.avgSeatMin ?? 0;
  const spend = outlet.spendCover ?? 0;
  const turns = +(covers / seats).toFixed(2);
  const occupancy = Math.min(100, Math.round((seated / seats) * 100));
  const efficiency = +(seated / (occTables || 1)).toFixed(2);

  return {
    ...base,
    occupancy,
    seats,
    seated,
    tables,
    occTables,
    covers,
    incoming,
    seatTime,
    noshow: outlet.noShow ?? 0,
    cancel: outlet.cancelRate ?? 0,
    efficiency,
    turns,
    revenue: Math.round(covers * spend),
    spend: Math.round(spend),
    sellRevenue: outlet.sellRevenue ?? 0,
    sellCount: outlet.sellCount ?? 0,
    sellAttach: outlet.sellAttach ?? 0,
    outlets: outlet.closed ? 0 : 1,
  };
}
